import { Trans } from 'react-i18next';

import { AppointmentReceptionService } from '@dreryk/egabinet-appointment-frontend-api';
import { VisitSettlement } from '@dreryk/egabinet-financial-settlement-frontend-api';

import { Translate, useTranslate } from '@dreryk/react-common';
import { Heading, StyledFlex, Text, TextSeparator, TextSeparatorType } from '@dreryk/react-components';

import receptionService from '../../../../receptionService';
import { AdjustmentDataText } from '../../../shared/adjustment/AdjustmentDataText';
import { createAdjustmentByFormBean } from '../../../shared/form/adjustment/adjustment.utils';
import { areAdvancePaymentsAvailable } from '../../../shared/form/VisitSettlementForm.utils';
import { MedicalServiceList } from '../../../shared/medicalServices/MedicalServiceList';
import { mapToVisitMedicalServicesTableBean } from '../../../shared/medicalServices/medicalServices.utils';
import { practitionerPriceDifferentFromServicePrice } from '../../../shared/price/price.utils';
import { visitSettlementTranslationKeys } from '../../../translations';
import { getAdvancePaymentMethodText } from '../../../../calendar/Timetable/shared/contextDialog/details/AppointmentAdvancePayments/AppointmentAdvancePaymentInfoItem.utils';

import { StyledPriceText } from './VisitSettlementCalendarTabContent.styled';

const t = visitSettlementTranslationKeys.extensions.calendar.tab.content;

type VisitSettlementCalendarTabContentProps = {
   readonly visitSettlement: VisitSettlement | null;
   readonly receptionServices: AppointmentReceptionService[];
};

// eslint-disable-next-line max-lines-per-function
export const VisitSettlementCalendarTabContent = (props: VisitSettlementCalendarTabContentProps): JSX.Element => {
   const translate = useTranslate();
   const { priceChangeReason, settlementStatus, paymentAmount, paymentMethod, adjustment, advancePayments } =
      props.visitSettlement ?? {};
   const medicalServices = props.visitSettlement?.medicalServices.map(mapToVisitMedicalServicesTableBean) ?? [];

   const showPriceChangeReasonSection =
      priceChangeReason || practitionerPriceDifferentFromServicePrice(medicalServices);
   const showPaymentSection = paymentAmount && paymentMethod && settlementStatus !== 'UNSETTLED';
   const showPaymentMethodDescription = showPaymentSection && paymentMethod.description;
   const showAdjustmentNoteSection = Boolean(adjustment?.adjustmentNote);

   const adjustmentBy = createAdjustmentByFormBean({
      adjustment: props.visitSettlement?.adjustment,
   });

   return (
      <StyledFlex direction={'column'} gap={'12px'}>
         <receptionService.calendar.settlement.ReceptionServiceList receptionServices={props.receptionServices} />
         <MedicalServiceList medicalServices={medicalServices} />

         {showPriceChangeReasonSection ? (
            <section>
               <Heading testId={'priceChangeReason'} type={'h3'} margin={'4px'} content={t.priceChangeReason} bold />
               <Text testId={'priceChangeReason'} whiteSpaces={'break-spaces'}>
                  {priceChangeReason ?? <Translate>{t.noPriceChangeReason}</Translate>}
               </Text>
            </section>
         ) : null}
         {showAdjustmentNoteSection ? (
            <section>
               <Heading testId={'adjustmentNote'} type={'h3'} margin={'4px'} content={t.adjustmentNote} bold />
               <Text testId={'adjustmentNote'} whiteSpaces={'break-spaces'}>
                  {adjustment!.adjustmentNote}
               </Text>
            </section>
         ) : null}
         {showPaymentSection ? (
            <StyledFlex as={'section'} direction={'column'}>
               <Heading testId={'payment'} type={'h3'} margin={'4px'} content={t.payment} bold />

               {areAdvancePaymentsAvailable(advancePayments) &&
                  advancePayments.map((advancePayment) => (
                     <TextSeparator key={advancePayment.initialTransactionId} separator={TextSeparatorType.SPACE}>
                        <StyledPriceText
                           testId={'advancePaymentAmount'}
                           value={advancePayment.remainingAmount.asNumber()}
                           stripDecimalZeroes
                        />
                        <Text testId={'advancePaymentMethod'}>
                           <Trans
                              i18nKey={t.advancePaymentMethodTextView}
                              values={{
                                 advancePaymentMethod: getAdvancePaymentMethodText(advancePayment, translate),
                              }}
                           />
                        </Text>
                     </TextSeparator>
                  ))}

               <TextSeparator separator={TextSeparatorType.COMMA_SPACE}>
                  <StyledPriceText testId={'paymentAmount'} value={paymentAmount.asNumber()} stripDecimalZeroes />
                  <Text testId={'paymentMethod'}>
                     {showPaymentMethodDescription ? (
                        <Trans
                           i18nKey={t.paymentMethodDescription}
                           values={{
                              method: translate(t.paymentMethodMap[paymentMethod.methodType]),
                              description: paymentMethod.description,
                           }}
                        />
                     ) : (
                        <Translate>{t.paymentMethodMap[paymentMethod.methodType]}</Translate>
                     )}
                  </Text>
               </TextSeparator>
            </StyledFlex>
         ) : null}
         {adjustmentBy ? <AdjustmentDataText author={adjustmentBy.author} date={adjustmentBy.date} /> : null}
      </StyledFlex>
   );
};
